import { Sparkles, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { SegmentedControl } from '@/components/ui/controls'
import { AddNote } from './AddNote'
import { NoteVisibility } from './NoteVisibility'
import { findTemplate } from '@/data/templates'
import { cachedNotes } from '@/data/meetings'
import type { Meeting } from '@/data/types'

/**
 * What an upcoming meeting unfolds into: what will be written afterwards.
 *
 * It lists what is actually configured rather than every template with a
 * switch. A tenant's template library grows; a wall of toggles turns into a
 * wall of OFF, and the two that matter get lost in it. Adding goes through the
 * same searchable `AddNote` control the written notes use, so configuring
 * before the meeting and adding after it are the same gesture.
 */
export function UpcomingNotes({
  meeting,
  onToggleTemplate,
  onScopeChange,
  onAddPromptNote,
  onToggleNoteShared,
  onRemoveNote,
  busy,
}: {
  meeting: Meeting
  onToggleTemplate: (templateId: string) => void
  onScopeChange: (scope: 'meeting' | 'series') => void
  onAddPromptNote: (prompt: string) => void
  onToggleNoteShared: (noteId: string, shared: boolean) => void
  onRemoveNote: (noteId: string) => void
  busy?: boolean
}) {
  // What will run on this meeting, once it has been opened. The list row itself
  // only knows how many.
  const notes = cachedNotes(meeting.id)

  // No background of its own — the open block already supplies it.
  return (
    <div className="border-t border-[var(--color-border)] px-3 py-3">
      <div className="mb-1.5 flex items-center justify-between gap-3">
        <p className="text-2xs text-[var(--color-fg-subtle)]">
          {meeting.noteScope === 'series'
            ? 'Written after every meeting in this series'
            : 'Written after this meeting'}
        </p>

        <div className="flex items-center gap-1">
          {meeting.isRecurring ? (
            <SegmentedControl
              size="sm"
              value={meeting.noteScope}
              onValueChange={onScopeChange}
              options={[
                { value: 'meeting', label: 'This meeting' },
                { value: 'series', label: 'Entire series' },
              ]}
            />
          ) : null}

          {/* No `write` mode: there is no transcript to write up yet. */}
          <AddNote
            notes={notes}
            modes={['template', 'prompt']}
            busy={busy}
            onAddFromTemplate={onToggleTemplate}
            onAddPrompt={onAddPromptNote}
            onAddBlank={() => {}}
          />
        </div>
      </div>

      {notes.length > 0 ? (
        <ul className="divide-y divide-[var(--color-border-subtle)] rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)]">
          {notes.map((note) => {
            const template = findTemplate(note.templateId)
            return (
              <li key={note.id} className="flex items-center gap-2 px-2.5 py-1.5">
                {note.prompt ? (
                  <Sparkles className="size-3 shrink-0 text-[var(--color-fg-subtle)]" />
                ) : null}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-xs text-[var(--color-fg)]">
                    {note.title}
                  </span>
                  <span className="block truncate text-2xs text-[var(--color-fg-subtle)]">
                    {note.prompt ?? template?.description ?? ''}
                  </span>
                </span>

                <NoteVisibility
                  title={note.title}
                  shared={note.sharedExternally}
                  busy={busy}
                  onChange={(shared) => onToggleNoteShared(note.id, shared)}
                />

                <Button
                  size="iconSm"
                  variant="ghost"
                  disabled={busy}
                  aria-label={`Remove ${note.title}`}
                  className="hover:text-[var(--color-danger)]"
                  onClick={() => onRemoveNote(note.id)}
                >
                  <Trash2 className="size-3" />
                </Button>
              </li>
            )
          })}
        </ul>
      ) : (
        <p className="rounded-[var(--radius-md)] border border-dashed border-[var(--color-border)] px-3 py-4 text-center text-2xs text-[var(--color-fg-subtle)]">
          Nothing will be written from this meeting yet.
        </p>
      )}
    </div>
  )
}
